import { FC, useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import { InnerhivePage } from '../InnerhivePage';
import { IonButton, IonButtons, IonToolbar } from '@ionic/react';
import { Text } from '../components/Text';
import { ActiveCareMapProviderV2 } from '../ActiveCareMapProvider';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CareMapState } from '../caremap/CareMapState';
import { ManageButton } from './ManageButton';
import { ICareMap } from '../caremap/ICareMap';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';
import { RemoveMeButton } from './RemoveMeButton';
import { CommunityFeedList } from './CommunityFeedList';
import { ICaremapNote } from '../notes/model/NotesClient';
import { AddCommunityPostWithQueryParamDrawer } from './AddCommunityPostWithQueryParamDrawer';
import { CommunityModelFactory } from '../share-caremap/CommunityModelFactory';
import { ViewCommunityPostWithQueryParamDrawer } from './ViewCommunityPostWithQueryParamDrawer';
import { AddPostButton } from './AddPostButton';
import { TemplateProvider } from '../notes/generic-notes/note-templates/TemplateProvider';
import { NoteTemplateMenu } from '../notes/generic-notes/note-templates/NoteTemplateMenu';
import { CareRecipientSwitcher } from '../navigation/CareRecipientSwitcher';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { getNoteTemplateTrackingLocation } from '../notes/generic-notes/NotesTrackingHelper';
import { CommunityTabsProvider } from './CommunityTabsProvider';
import { AddCareMapButton } from '../caremap/AddCareMapButton';
import { CommunityNoCareMapPage } from './CommunityNoCareMapPage';

const useStyles = createUseStyles(() => ({
    toolbar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },

    image: {
        maxWidth: 500,
        margin: 'auto',
        padding: '30px 60px 0 20px'
    }
}));

export const CommunityFeedPage: FC<{ careMapId: string; }> = ({ careMapId }) => {
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    return (
        <ActiveCareMapProviderV2 careMapId={careMapId}>
            <CommunityFeedPageWithCareMap careMapId={careMapId} />
        </ActiveCareMapProviderV2>
    );
};

const CommunityFeedPageWithCareMap: FC<{ careMapId: string; }> = ({ careMapId }) => {
    const classes = useStyles();

    const careMapState = useInstance(CareMapState);

    const mixpanel = useInstance(MixPanelEventEmitter);

    const addPostDrawer = useInstance(AddCommunityPostWithQueryParamDrawer);
    const viewPostDrawer = useInstance(ViewCommunityPostWithQueryParamDrawer);
    const templates = useInstance(TemplateProvider).getTemplates({section: 'patient'});

    const factory = useInstance(CommunityModelFactory);
    useSubscription(() => factory);
    const community = factory.forActiveCaremap();

    const notesModelFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesModelFactory);

    const tabsProvider = useInstance(CommunityTabsProvider);

    // load data on mount
    useEffect(() => {
        community.load();
    }, [community]);

    useEffect(() => {
        mixpanel.track('Community Feed Tab Opened');
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const careMap = careMapState.getActiveCaremap() as ICareMap;
    const incompleteCareMap = careMapState.getIncompleteCareMap();

    if (incompleteCareMap) {
        return <CommunityNoCareMapPage />;
    }

    const model = careMap ? notesModelFactory.forActiveCaremap() : undefined;

    // never show private notes on community page
    const posts = model?.getNotes().filter(post => !post.isPrivate);

    const hasPosts = Boolean(posts?.length);

    const name = careMapState.getPatientFirstName(careMap);
    const supportMsg = (name ? `supporting ${name}` : 'your support');

    const collaboratorCount = community.getCollaboratorCount();
    const followers = community.isLoaded() && collaboratorCount ? `${collaboratorCount} Supporter${collaboratorCount !== 1 ? 's' : ''}` : '';

    const self = Boolean(careMap?.initialWizardAnswers['recipient-self']);
    const whose = (name && !self ? `${name}'s` : 'your');

    const canManage = Boolean(careMap?.links.permissions || careMap?.links.createViewerAccessToken);
    const canEdit = careMap?.links.self.actions.includes('edit-notes');

    const addComment = (post: ICaremapNote) => {
        viewPostDrawer.open({ postId: post.noteId, addComment: true });
    };

    const viewPost = (post: ICaremapNote) => {
        viewPostDrawer.open({ postId: post.noteId });
    };

    const addPost = (template?: any) => {
        addPostDrawer.open({ templateId: template?.templateId });

        if (template) {
            mixpanel.track('Note Template Applied', () => ({
                action: 'insert',
                template: template.label,
                templateId: template.templateId,
                location: getNoteTemplateTrackingLocation(template.target)
            }));
        }
    };

    const groceriesTemplate = templates.find(t => t.templateId === 'groceries');
    const rideTemplate = templates.find(t => t.templateId === 'ride');

    return (
        <InnerhivePage
            data-id="community-feed-page"
            title="Community"
            tabs={tabsProvider.getTabs()}
        >
            {model?.hasLoaded &&
                <>
                    <IonToolbar>
                        {incompleteCareMap ? (
                            <AddCareMapButton />
                        ) : (
                            <>
                                <div style={{margin: '4px 0 12px 4px'}}>
                                    <CareRecipientSwitcher />
                                </div>
                                <div className={classes.toolbar} data-loaded={community.isLoaded()}>
                                    {followers && (
                                        <div style={{paddingLeft: 6}}><Text type="detail">{followers}</Text></div>
                                    )}&nbsp;
                                    <IonButtons slot="end">
                                        {canManage && (
                                            <ManageButton careMapId={careMapId} />
                                        )}
                                        {!canManage && (
                                            <RemoveMeButton />
                                        )}
                                    </IonButtons>
                                </div>
                            </>
                        )}
                    </IonToolbar>

                    {canEdit && (
                        <>
                            <AddPostButton onClick={() => addPost()} />
                            <div style={{display: 'flex'}}>
                                <IonButton
                                    fill="outline"
                                    onClick={() => addPost(groceriesTemplate)}
                                    data-id={`note-template-${groceriesTemplate?.templateId}-button`}
                                >
                                    {groceriesTemplate?.label}
                                </IonButton>
                                <IonButton
                                    fill="outline"
                                    onClick={() => addPost(rideTemplate)}
                                    data-id={`note-template-${rideTemplate?.templateId}-button`}
                                >
                                    {rideTemplate?.label}
                                </IonButton>
                            </div>
                            <NoteTemplateMenu
                                hasButtons
                                onItemClick={template => addPost(template)}
                                templates={templates.filter(t => t.templateId !== 'groceries' && t.templateId !== 'ride')}
                            />
                        </>
                    )}

                    {!hasPosts && !canEdit && (
                        <>
                            <p>
                                <Text>
                                    Thank you so much for {supportMsg}! There are no community posts yet – be sure to check back soon.
                                </Text>
                            </p>
                            <div className={classes.image}>
                                <img src="/assets/images/share/spread-love.png" alt="" />
                            </div>
                        </>
                    )}

                    {!hasPosts && canEdit && (
                        <>
                            <p>
                                <Text>
                                    Welcome {whose} community to Innerhive with an intro post! Here’s an example:
                                </Text>
                            </p>
                            <p>
                                <Text type="detail">
                                    <i style={{fontSize: 14}}>
                                    Hi everyone! I want to update you on Grandpa Joe—he’s been diagnosed with dementia, and I’ll be helping with his daily care. With many appointments and a large care team, I’ll use Innerhive to stay organized and keep you informed. Your support means a lot, even if I can’t always reply.
                                    </i>
                                </Text>
                            </p>
                            <div className={classes.image}>
                                <img src="/assets/images/share/spread-love.png" alt="" />
                            </div>
                        </>
                    )}

                    <CommunityFeedList
                        posts={posts}
                        postClick={viewPost}
                        addComment={addComment}
                    />
                </>
            }
        </InnerhivePage>
    );
};
